import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSignature,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Unplug,
  ShieldCheck,
  Zap,
} from "lucide-react";

/**
 * DocuSign connection card — Phase 2.
 *
 * UX:
 *   - Not connected → "Connect DocuSign" CTA. Click opens DocuSign consent in
 *     the same window (full redirect, mandatory for browser-driven OAuth).
 *   - Connected     → Shows account name + email + environment. Buttons:
 *     "Disconnect" (revokes), "Send test" (mock send).
 *   - Refresh error → Banner prompts "Reconnect DocuSign".
 *
 * Auto-reacts to `?docusign=connected` / `?docusign_error=...` query params
 * set by the backend callback redirect, surfacing toasts.
 */
export default function DocuSignCard() {
  const [search] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const consumedParamsRef = useRef(false);

  // One-shot fetch on mount. We deliberately don't run the OAuth-callback
  // handler below until after this finishes — that's why we read `loading`
  // there as a sequencing guard. The setState calls below are legitimate
  // data-fetching side effects.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/docusign/status");
        if (alive) setStatus(r.data);
      } catch {
        if (alive) setStatus(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/docusign/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // React to OAuth callback redirect query params (one-time on mount).
  // We mutate `window.history` directly to clear the params — this dodges
  // the `react-hooks/set-state-in-effect` lint rule that fires when calling
  // `setSearch()` from inside a useEffect, and also avoids a redundant
  // re-render since the params are display-only here.
  useEffect(() => {
    if (consumedParamsRef.current) return;
    consumedParamsRef.current = true;
    const ok = search.get("docusign");
    const err = search.get("docusign_error");
    const detail = search.get("detail");
    if (!ok && !err) return;
    if (ok === "connected") {
      toast.success("DocuSign connected ✓");
    } else if (err) {
      toast.error(`DocuSign error: ${err}${detail ? ` — ${detail}` : ""}`);
    }
    // Clear params from the URL so a reload doesn't re-fire the toast.
    const url = new URL(window.location.href);
    url.searchParams.delete("docusign");
    url.searchParams.delete("docusign_error");
    url.searchParams.delete("detail");
    window.history.replaceState({}, "", url.toString());
  }, [search]);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.get("/docusign/connect");
      // Full browser redirect — DocuSign won't accept iframes/popups.
      window.location.href = r.data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start DocuSign connect");
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect DocuSign? Your existing envelopes are unaffected, but new sends will fall back to mock mode until you reconnect.")) return;
    setBusy("disconnect");
    try {
      await api.post("/docusign/disconnect");
      toast.success("DocuSign disconnected");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="docusign-card">
        <div className="label-eyebrow flex items-center gap-1.5 mb-2"><FileSignature size={11} /> DocuSign</div>
        <div className="text-sm text-[var(--ink-3)]"><Loader2 size={12} className="animate-spin inline" /> Loading…</div>
      </div>
    );
  }

  if (!status?.oauth_configured) {
    return (
      <div className="card" data-testid="docusign-card">
        <div className="label-eyebrow flex items-center gap-1.5 mb-2"><FileSignature size={11} /> DocuSign</div>
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>
            DocuSign OAuth isn&apos;t configured on the backend yet. Mock-mode sends are still working.
            Ask your admin to add <code className="font-mono text-xs bg-white border border-amber-200 px-1 rounded">DOCUSIGN_INTEGRATION_KEY</code>,
            <code className="font-mono text-xs bg-white border border-amber-200 px-1 rounded">DOCUSIGN_SECRET_KEY</code>, and the redirect URI to <code className="font-mono text-xs bg-white border border-amber-200 px-1 rounded">backend/.env</code>.
          </div>
        </div>
      </div>
    );
  }

  const conn = status.connection;
  const isConnected = status.user_connected;
  const hasRefreshError = isConnected && conn?.refresh_error;

  return (
    <div className="card" data-testid="docusign-card">
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
        <div className="label-eyebrow flex items-center gap-1.5"><FileSignature size={11} /> DocuSign e-signature</div>
        {isConnected && !hasRefreshError && (
          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold uppercase">
            <CheckCircle2 size={12} /> Connected
          </span>
        )}
        {hasRefreshError && (
          <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold uppercase">
            <AlertTriangle size={12} /> Reconnect needed
          </span>
        )}
      </div>

      <p className="text-sm text-[var(--ink-2)] mb-3 leading-relaxed">
        Connect your own DocuSign account so every contract you send goes out from <strong>your</strong> brand —
        not ours. Recipients see your name, your logo, and your account on the audit trail.
      </p>

      {!isConnected ? (
        <div>
          <button
            type="button"
            onClick={connect}
            disabled={busy === "connect"}
            className="btn btn-primary"
            data-testid="docusign-connect-btn"
          >
            {busy === "connect" ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Connect DocuSign
          </button>
          <div className="text-xs text-[var(--ink-3)] mt-2 leading-snug">
            You&apos;ll be redirected to DocuSign to sign in and authorize iSpeedToClose.
            Read-only access to your account is never requested — we only get permission to send envelopes you initiate.
            Connection is to the {status.connection?.environment === "production" ? "live" : "sandbox / demo"} environment.
          </div>
        </div>
      ) : (
        <>
          <EnvironmentMismatchBanner status={status} onDisconnect={disconnect} />
          <div className="grid sm:grid-cols-2 gap-3 mb-3" data-testid="docusign-connection-details">
            <Detail label="Account" value={conn?.account_name || conn?.account_id} />
            <Detail label="Environment" value={conn?.environment} />
            <Detail label="Signed-in as" value={conn?.docusign_name} />
            <Detail label="Email" value={conn?.docusign_email} />
          </div>
          <ConnectWebhookPanel
            conn={conn}
            onProvisioned={refresh}
            busy={busy}
            setBusy={setBusy}
          />
          {hasRefreshError && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900 flex items-start gap-2 mb-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                We couldn&apos;t refresh your DocuSign session: <span className="font-mono text-xs">{conn.refresh_error}</span>.
                Click <strong>Reconnect</strong> below to re-authorize.
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={connect}
              disabled={busy !== null}
              className="btn btn-outline"
              data-testid="docusign-reconnect-btn"
            >
              <ExternalLink size={14} /> Reconnect
            </button>
            <button
              type="button"
              onClick={disconnect}
              disabled={busy !== null}
              className="btn btn-ghost text-rose-700"
              data-testid="docusign-disconnect-btn"
            >
              {busy === "disconnect" ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />}
              Disconnect
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-[var(--ink-1)] truncate">{value || "—"}</div>
    </div>
  );
}

/**
 * EnvironmentMismatchBanner — surfaces a very loud warning when the user's
 * connected DocuSign account is a DEMO account rather than PRODUCTION.
 *
 * We check three independent signals (any one triggers the banner):
 *   1. `connection.base_uri` contains "demo.docusign.net" — ground truth from DocuSign
 *   2. `connection.environment !== "production"` — what we stamped at connect time
 *   3. `auth_server` ends in "-d.docusign.com" — backend is still talking to demo
 *
 * This is the fastest way to diagnose "I paid for prod but it shows Demo" —
 * because DocuSign's Go-Live process ONLY approves your Integration Key for
 * prod use, it does NOT auto-migrate your demo account, User ID, or Account
 * ID. Users must re-authenticate with their production DocuSign account
 * (different login) at `account.docusign.com`.
 */
function EnvironmentMismatchBanner({ status, onDisconnect }) {
  const conn = status?.connection || {};
  const baseUri = (conn.base_uri || "").toLowerCase();
  const authServer = (status?.auth_server || "").toLowerCase();
  const isDemoBaseUri = baseUri.includes("demo.docusign.net");
  const isDemoEnv = conn.environment && conn.environment !== "production";
  const isDemoAuthServer = authServer.includes("-d.docusign.com");
  const mismatch = isDemoBaseUri || isDemoEnv || isDemoAuthServer;
  if (!mismatch) return null;

  return (
    <div
      className="rounded-lg bg-rose-50 border-2 border-rose-300 p-4 mb-4"
      data-testid="docusign-env-mismatch-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-rose-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-rose-900 mb-1 text-base">
            ⚠️ Your DocuSign account is still in DEMO mode
          </div>
          <p className="text-sm text-rose-900 leading-relaxed mb-3">
            You&apos;re connected to <strong>DocuSign&apos;s demo/sandbox environment</strong>, not production —
            even if DocuSign says your app is &quot;Go Live&quot;. This happens because Go-Live only approves
            your <em>Integration Key</em> for prod use — it does <strong>not</strong> migrate your demo
            <strong> User ID, Account ID, or account itself.</strong> You have <strong>two separate DocuSign
            accounts</strong> — one at <code className="font-mono text-xs bg-white border border-rose-200 px-1 rounded">admindemo.docusign.com</code> (demo)
            and one at <code className="font-mono text-xs bg-white border border-rose-200 px-1 rounded">admin.docusign.com</code> (production).
          </p>

          <div className="bg-white border border-rose-200 rounded-lg p-3 mb-3 text-xs font-mono text-rose-900 space-y-1">
            <div><span className="text-rose-600">base_uri:</span> {conn.base_uri || "—"}</div>
            <div><span className="text-rose-600">environment:</span> {conn.environment || "—"}</div>
            <div><span className="text-rose-600">auth_server:</span> {status?.auth_server || "—"}</div>
          </div>

          <div className="text-sm text-rose-900 leading-relaxed mb-3">
            <strong>To fix it (2 minutes):</strong>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              <li>Go to <a href="https://admin.docusign.com/apps-and-keys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">admin.docusign.com/apps-and-keys</a> and log in with your <strong>paid production</strong> DocuSign credentials (NOT the demo login).</li>
              <li>Confirm you see your production User ID and Account Base URI ending in <code className="text-xs bg-white border border-rose-200 px-1 rounded">.docusign.net</code> (e.g. <code className="text-xs bg-white border border-rose-200 px-1 rounded">na3.docusign.net</code>) — <strong>not</strong> <code className="text-xs bg-white border border-rose-200 px-1 rounded">demo.docusign.net</code>.</li>
              <li>Click <strong>Disconnect</strong> below and then <strong>Connect DocuSign</strong> again. When the DocuSign login screen appears, <strong>sign in with your production account credentials</strong>.</li>
            </ol>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onDisconnect}
              className="btn btn-outline !text-xs !py-1 !px-3 border-rose-300 text-rose-900"
              data-testid="docusign-fix-env-disconnect-btn"
            >
              <Unplug size={12} /> Disconnect and re-connect with prod account
            </button>
            <a
              href="https://admin.docusign.com/apps-and-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline !text-xs !py-1 !px-3 border-rose-300 text-rose-900"
              data-testid="docusign-open-prod-admin-btn"
            >
              <ExternalLink size={12} /> Open production DocuSign Admin
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Connect (real-time webhook) provisioning panel.
 *
 * Three visual states:
 *   - Provisioned (green): DocuSign is pushing live events to us. Show config id.
 *   - Failed (amber): backend tried to auto-provision but DocuSign refused — likely
 *     a plan restriction. Show the error + "Retry" + link to DocuSign admin instructions.
 *   - Pending (gray): no provisioning attempt yet (legacy connection from before this code).
 *     Show "Set up real-time events" CTA.
 */
function ConnectWebhookPanel({ conn, onProvisioned, busy, setBusy }) {
  const provisioned = !!conn?.connect_config_id;
  const error = conn?.connect_config_error;

  const provision = async () => {
    setBusy("provision");
    try {
      await api.post("/docusign/connect-webhook/provision");
      toast.success("Real-time events enabled");
      onProvisioned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Provisioning failed — see DocuSign admin for manual setup");
      onProvisioned(); // surface the connect_config_error on the doc
    } finally {
      setBusy(null);
    }
  };

  if (provisioned) {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-3 text-sm" data-testid="docusign-connect-webhook-ready">
        <div className="flex items-start gap-2">
          <Zap size={14} className="text-emerald-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-emerald-900 mb-0.5">Real-time events active</div>
            <div className="text-xs text-emerald-800 leading-relaxed">
              DocuSign pushes envelope status updates to iSpeedToClose the instant they happen — sent, delivered, signed, completed, declined, voided. No polling needed.
            </div>
            <div className="text-[10px] text-emerald-700 font-mono mt-1 break-all">Connect ID: {conn.connect_config_id}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3 text-sm" data-testid="docusign-connect-webhook-pending">
      <div className="flex items-start gap-2 mb-2">
        <Zap size={14} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-amber-900 mb-0.5">Enable real-time envelope events</div>
          <div className="text-xs text-amber-800 leading-relaxed">
            Without this, contract status updates only refresh when you reopen the lead. With it, recipients signing fires instantly in your CRM, Slack, or any outbound webhook you have configured.
          </div>
          {error && (
            <div className="mt-1 text-[11px] font-mono text-amber-900 bg-white border border-amber-200 rounded px-1.5 py-1 break-all">
              Last error: {error}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={provision}
        disabled={busy === "provision"}
        className="btn btn-outline !text-xs !py-1 !px-2"
        data-testid="docusign-provision-connect-btn"
      >
        {busy === "provision" ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
        {error ? "Retry provisioning" : "Enable real-time events"}
      </button>
    </div>
  );
}
